import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

type AdminModeContextType = {
  isAdminMode: boolean;
  toggleMode: () => void;
  setAdminMode: (mode: boolean) => void;
  canAccessAdminMode: boolean;
};

const AdminModeContext = createContext<AdminModeContextType | undefined>(undefined);

export function AdminModeProvider({ children }: { children: ReactNode }) {
  const { isTenantOwner } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const isOnAdminRoute = location.pathname.startsWith('/admin');
  const [isAdminMode, setIsAdminMode] = useState(isOnAdminRoute);

  useEffect(() => {
    setIsAdminMode(location.pathname.startsWith('/admin'));
  }, [location.pathname]);

  const toggleMode = () => {
    if (!isTenantOwner) return;
    
    if (isAdminMode) {
      navigate('/dashboard');
    } else {
      navigate('/admin/dashboard');
    }
  };

  const setAdminModeValue = (mode: boolean) => {
    if (!isTenantOwner) return;
    
    if (mode && !isAdminMode) {
      navigate('/admin/dashboard');
    } else if (!mode && isAdminMode) {
      navigate('/dashboard');
    }
  };

  return (
    <AdminModeContext.Provider
      value={{
        isAdminMode,
        toggleMode,
        setAdminMode: setAdminModeValue,
        canAccessAdminMode: isTenantOwner,
      }}
    >
      {children}
    </AdminModeContext.Provider>
  );
}

export function useAdminMode() {
  const context = useContext(AdminModeContext);
  if (context === undefined) {
    throw new Error('useAdminMode must be used within an AdminModeProvider');
  }
  return context;
}
